import AppDispatcher    from '~/dispatchers/app_dispatcher';
import APIRequest       from '~/lib/api_request';
import VideoActionTypes from '~/types/video_action_types';
import GlobalContainer     from '~/components/global_container';
import { getRestrictionScope } from '~/helpers/staffRestrictionHelper';
import tableConfig from '~/components/pages/agent_dashboard/table_config';
import _lodash from 'lodash';
import BulkSelectActions from '~/actions/bulk_select_actions';

let videosRequest;
let videoRequest;
let createVideoRequest;
let updateVideoRequest;
let deleteVideoRequest;
let bulkUpdateVideosRequest;
let bulkDeleteVideosRequest;
let loadCategoriesRequest;
const videoTableScopeRequest = {};

const VideoActions = {
  defaultOptions() {
    const staffRestriction = getRestrictionScope();

    return {
      ownerScope: localStorage.ownerScope || staffRestriction || 'all',
      page:       1,
      size:       parseInt(localStorage.videoScopeSize, 10) || 10,
      searchData: {},
    };
  },

  loadLibraryVideos(options = {}) {
    const opts = Object.assign(this.defaultOptions(), options);

    const query = {
      page: opts.page,
      per:  opts.size,
      s:    opts.search || {},
    };

    AppDispatcher.dispatch({
      type: VideoActionTypes.LOAD_LIBRARY_VIDEOS,
    });

    if (videosRequest) {
      APIRequest.abort(videosRequest);
    }

    videosRequest = APIRequest.get({
      resource: '/v1/videos',
      data:     query,
    });

    videosRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: VideoActionTypes.LOAD_LIBRARY_VIDEOS_FAIL,
          error,
        });
        GlobalContainer.notify('Failed to load videos', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type:     VideoActionTypes.LOAD_LIBRARY_VIDEOS_DONE,
        response: response.body,
      });
    });
  },

  loadVideos(table = tableConfig.videosTable(), options = {}) {
    const opts = Object.assign(this.defaultOptions(), options);

    const query = {
      video_scope: table?.scope || 'videos',
      scope:       opts.ownerScope,
      page:        opts.page,
      per:         opts.size,
      s:           opts.searchData || {},
    };

    const { sortColumn, sortDirection } = opts;

    if (sortColumn && sortDirection) {
      query.lead_sort = `${sortColumn}-${sortDirection}`;
    }

    AppDispatcher.dispatch({
      type: VideoActionTypes.LOAD_VIDEOS,
      table,
    });

    if (videoTableScopeRequest[table?.scope]) {
      APIRequest.abort(videoTableScopeRequest[table?.scope]);
    }

    videoTableScopeRequest[table?.scope] = APIRequest.get({
      resource: '/v1/videos',
      data:     query,
    });

    videoTableScopeRequest[table?.scope].end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: VideoActionTypes.LOAD_VIDEOS_FAIL,
          table,
          error,
        });
        GlobalContainer.notify('Failed to load videos', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type:     VideoActionTypes.LOAD_VIDEOS_DONE,
        table,
        response: response.body,
        query,
      });
    });
  },

  loadTrashedVideos(table = tableConfig.trashVideosTable(), options = {}) {
    const opts = Object.assign(this.defaultOptions(), options);

    const query = {
      video_scope: table.scope,
      scope:       opts.ownerScope,
      page:        opts.page,
      per:         opts.size,
      s:           opts.searchData || {},
    };

    AppDispatcher.dispatch({
      type: VideoActionTypes.LOAD_TRASHED_VIDEOS,
      table,
    });

    if (videoTableScopeRequest[table.scope]) {
      APIRequest.abort(videoTableScopeRequest[table.scope]);
    }

    videoTableScopeRequest[table.scope] = APIRequest.get({
      resource: '/v1/videos/trashed_videos',
      data:     query,
    });

    videoTableScopeRequest[table.scope].end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: VideoActionTypes.LOAD_TRASHED_VIDEOS_FAIL,
          table,
          error,
        });
        GlobalContainer.notify('Failed to load trashed videos', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type:     VideoActionTypes.LOAD_TRASHED_VIDEOS_DONE,
        table,
        response: response.body,
        query,
      });
    });
  },

  empty_videos_trash() {
    APIRequest.abort(deleteVideoRequest);

    AppDispatcher.dispatch({
      type: VideoActionTypes.PERMANENT_DELETE_VIDEO,
    });

    deleteVideoRequest = APIRequest.delete({
      resource: '/v1/video_bulk_actions/empty_videos_trash',
    });

    deleteVideoRequest.end((error) => {
      if (error) {
        const errors = error?.response?.body?.errors || 'Failed to permanently delete the videos. Please try again later.';

        AppDispatcher.dispatch({
          type:  VideoActionTypes.PERMANENT_DELETE_VIDEO_FAIL,
          error: errors,
        });

        GlobalContainer.notify(errors, 'error');
        return;
      }

      AppDispatcher.dispatch({
        type: VideoActionTypes.PERMANENT_DELETE_VIDEO_DONE,
      });

      GlobalContainer.notify('Bulk delete in progress, this may take some time.');
      setTimeout(() => {
        VideoActions.loadTrashedVideos(undefined, { page: 1 });
      }, 500);
    });
  },

  loadVideo(videoID) {
    APIRequest.abort(videoRequest);

    AppDispatcher.dispatch({
      type: VideoActionTypes.LOAD_VIDEO,
    });

    videoRequest = APIRequest.get({
      resource: `/v1/videos/${videoID}`,
    });

    videoRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: VideoActionTypes.LOAD_VIDEO_FAIL,
          error,
        });

        return;
      }

      AppDispatcher.dispatch({
        type:   VideoActionTypes.LOAD_VIDEO_DONE,
        video: response.body,
      });
    });
  },

  createVideo(params, { page = 1, loadVideos = false, searchData = {} } = {}, table = null) {
    APIRequest.abort(createVideoRequest);

    AppDispatcher.dispatch({
      type: VideoActionTypes.CREATE_VIDEO,
    });

    createVideoRequest = APIRequest.post({
      resource: '/v1/videos/',
      data:     { ...params },
    });

    createVideoRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: VideoActionTypes.CREATE_VIDEO_FAIL,
          error,
        });

        return;
      }

      AppDispatcher.dispatch({
        type:   VideoActionTypes.CREATE_VIDEO_DONE,
        video: response.body,
      });

      if (loadVideos) {
        const tableToUse = table || {
          scope: 'videos',
          title: 'Videos',
          resource: '/v1/videos',
          filterable: true,
          searchable: true,
          sortBy: 'uploaded_at',
          sortDirection: 'desc',
          timestamp: {
            column: 'uploaded_at',
            title: 'Uploaded At',
          }
        };

        VideoActions.loadVideos(tableToUse, {
          page,
          searchData,
          ownerScope: localStorage.ownerScope || 'all',
        });
      }
    });
  },

  updateVideo(video, { page = 1, loadVideos = true, searchData = {} } = {}, table = null) {
    APIRequest.abort(updateVideoRequest);

    AppDispatcher.dispatch({
      type: VideoActionTypes.UPDATE_VIDEO,
      video,
    });

    updateVideoRequest = APIRequest.put({
      resource: `/v1/videos/${video.id}`,
      data: { ...video },
    });

    updateVideoRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: VideoActionTypes.UPDATE_VIDEO_FAIL,
          error,
        });
        GlobalContainer.notify('Failed to save the video. Please try again later.', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type: VideoActionTypes.UPDATE_VIDEO_DONE,
        video: response.body,
      });

      if (loadVideos) {
        // Use the provided table config or get default from tableConfig
        const tableToUse = table || {
          scope: 'videos',
          title: 'Videos',
          resource: '/v1/videos',
          filterable: true,
          searchable: true,
          sortBy: 'uploaded_at',
          sortDirection: 'desc',
          timestamp: {
            column: 'uploaded_at',
            title: 'Uploaded At',
          }
        };

        GlobalContainer.notify(`Video saved successfully.`, 'success');

        VideoActions.loadVideos(tableToUse, {
          page,
          searchData,
          ownerScope: localStorage.ownerScope || 'all',
        });
      }
    });
  },

  deleteVideo(video, { page = 1, loadVideos = true, searchData = {} } = {}, table = null) {
    APIRequest.abort(deleteVideoRequest);

    AppDispatcher.dispatch({
      type: VideoActionTypes.DELETE_VIDEO,
      video,
    });

    deleteVideoRequest = APIRequest.delete({
      resource: `/v1/videos/${video.id}/move_to_trash`,
    });

    deleteVideoRequest.end((error, _response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: VideoActionTypes.DELETE_VIDEO_FAIL,
          error,
        });

        GlobalContainer.notify('Failed to delete the video. Please try again later.', 'error');

        return;
      }

      AppDispatcher.dispatch({
        type: VideoActionTypes.DELETE_VIDEO_DONE,
      });

      GlobalContainer.notify('Video has been deleted', 'success');
      if (table) {
        BulkSelectActions.deselectVideoItem(table, video);
      }

      if (loadVideos) {
        VideoActions.loadVideos(table,
          {
            page,
            searchData,
            ownerScope: localStorage.ownerScope || 'all',
          }
        );
      }
    });
  },

  restoreVideo(videoID, currentPage, table = null) {
    APIRequest.abort(updateVideoRequest);

    AppDispatcher.dispatch({
      type: VideoActionTypes.RESTORE_VIDEO,
      videoID,
    });

    updateVideoRequest = APIRequest.put({
      resource: `/v1/videos/${videoID}/restore`,
    });

    updateVideoRequest.end((error, response) => {
      if (error) {
        const errors = error.response && error.response.body && error.response.body.errors
          ? error.response.body.errors
          : 'Failed to restore the video. Please try again later.';

        AppDispatcher.dispatch({
          type:  VideoActionTypes.RESTORE_VIDEO_FAIL,
          error: errors,
        });

        GlobalContainer.notify(errors, 'error');
        return;
      }

      AppDispatcher.dispatch({
        type:     VideoActionTypes.RESTORE_VIDEO_DONE,
        video: response.body,
      });

      GlobalContainer.notify('Video has been restored', 'success');

      if (table) {
        BulkSelectActions.deselectTrashVideoItem(table, { id: videoID });
      }

      VideoActions.loadTrashedVideos(undefined, { page: currentPage });
    });
  },

  permanentDeleteVideo(videoID, currentPage, table = null) {
    APIRequest.abort(deleteVideoRequest);

    AppDispatcher.dispatch({
      type: VideoActionTypes.PERMANENT_DELETE_VIDEO,
      videoID,
    });

    deleteVideoRequest = APIRequest.delete({
      resource: `/v1/videos/${videoID}`,
    });

    deleteVideoRequest.end((error, _response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: VideoActionTypes.PERMANENT_DELETE_VIDEO_FAIL,
          error,
        });

        GlobalContainer.notify('Failed to permanently delete the video. Please try again later.', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type: VideoActionTypes.PERMANENT_DELETE_VIDEO_DONE,
      });

      GlobalContainer.notify('Video has been permanently deleted', 'success');
      if (table) {
        BulkSelectActions.deselectTrashVideoItem(table, { id: videoID });
      }

      VideoActions.loadTrashedVideos(undefined, { page: currentPage });
    });
  },

  loadCategories() {
    APIRequest.abort(loadCategoriesRequest);

    loadCategoriesRequest = APIRequest.get({
      resource: '/v1/videos/categories',
    });

    loadCategoriesRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: VideoActionTypes.LOAD_CATEGORIES_FAIL,
          error,
        });
        GlobalContainer.notify('Failed to load video categories', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type:       VideoActionTypes.LOAD_CATEGORIES_DONE,
        categories: response.body,
      });
    });
  },

  switchVideoScopeSize(widgets, videoScopeSize, searchData) {
    AppDispatcher.dispatch({
      type: VideoActionTypes.SWITCH_VIDEO_SCOPE_SIZE,
      videoScopeSize,
    });
    localStorage.videoScopeSize = videoScopeSize;

    const table = widgets.tables[0];
    const options = { size: videoScopeSize, page: 1, searchData };
    if (table.scope === 'videos') {
      VideoActions.loadVideos(table, options);
    } else if (table.scope === 'trash_videos') {
      VideoActions.loadTrashedVideos(table, options);
    }
  },

  bulkUpdateVideos(table, videos, bulkUpdateData, options={}) {
    APIRequest.abort(bulkUpdateVideosRequest);

    const staffRestriction = getRestrictionScope();

    AppDispatcher.dispatch({
      type:       VideoActionTypes.BULK_UPDATE_VIDEOS,
      tableScope: table.scope,
      videos,
      bulkUpdateData,
    });

    const scope = localStorage.ownerScope || staffRestriction || 'all';
    const updateData = {
      video_scope: table.scope,
      scope,
    };

    if (typeof videos === 'string') {
      updateData.ids = 'all';
    } else {
      updateData.ids = _lodash.map(videos, 'id');
    }

    const { page, s, per: size } = options;
    const data = { ...updateData, ...bulkUpdateData, s, scope };

    bulkUpdateVideosRequest = APIRequest.put({
      resource: '/v1/video_bulk_actions/update_videos',
      data,
    });

    bulkUpdateVideosRequest.end((error) => {
      if (error) {
        AppDispatcher.dispatch({
          type:       VideoActionTypes.BULK_UPDATE_VIDEOS_FAIL,
          tableScope: table.scope,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to update videos', 'error');
      } else {
        AppDispatcher.dispatch({
          type:       VideoActionTypes.BULK_UPDATE_VIDEOS_DONE,
          tableScope: table.scope,
        });

        BulkSelectActions.deselectAllItems(table);

        GlobalContainer.notify(
          'Bulk action in progress, this may take some time.',
          'success',
        );

        VideoActions.loadVideos(table, { page, size, searchData: s });
      }
    });
  },

  bulkDeleteVideos(table, videos, bulkDeleteData, searchOptions) {
    APIRequest.abort(bulkDeleteVideosRequest);

    const staffRestriction = getRestrictionScope();
    const scope = localStorage.ownerScope || staffRestriction || 'all';
    const isAllVideos = typeof videos === 'string';

    AppDispatcher.dispatch({
      type:       VideoActionTypes.BULK_DELETE_VIDEOS,
      tableScope: table.scope,
      videos,
      bulkDeleteData,
    });

    const deleteData = {
      video_scope: table.scope,
      scope:       scope,
      ids:         isAllVideos ? 'all' : _lodash.map(videos, 'id'),
    };

    const { s } = searchOptions;
    const data = { ...deleteData, ...bulkDeleteData, s, scope };

    bulkDeleteVideosRequest = APIRequest.delete({
      resource: '/v1/video_bulk_actions/delete_videos',
      data,
    });

    bulkDeleteVideosRequest.end((error) => {
      if (error) {
        AppDispatcher.dispatch({
          type:       VideoActionTypes.BULK_DELETE_VIDEOS_FAIL,
          tableScope: table.scope,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to delete videos', 'error');
      } else {
        AppDispatcher.dispatch({
          type:       VideoActionTypes.BULK_DELETE_VIDEOS_DONE,
          tableScope: table.scope,
        });

        BulkSelectActions.deselectAllItems(table);

        GlobalContainer.notify(
          'Bulk delete in progress, this may take some time.',
          'success',
        );
        VideoActions.loadVideos(table);
      }
    });
  },

  bulkRestoreTrashVideos(table, trashVideos, bulkRestoreData) {
    const staffRestriction = getRestrictionScope();
    const scope = localStorage.ownerScope || staffRestriction || 'all';
    const isAllVideos = typeof trashVideos === 'string';

    AppDispatcher.dispatch({
      type:       VideoActionTypes.BULK_RESTORE_TRASH_VIDEOS,
      tableScope: table.scope,
      trashVideos,
      bulkRestoreData,
    });

    const restoreData = {
      video_scope: table.scope,
      scope:       scope,
      ids:         isAllVideos ? 'all' : _lodash.map(trashVideos, 'id'),
    };

    const data = { ...restoreData, ...bulkRestoreData };

    bulkUpdateVideosRequest = APIRequest.put({
      resource: '/v1/video_bulk_actions/restore_videos',
      data,
    });

    bulkUpdateVideosRequest.end((error) => {
      if (error) {
        AppDispatcher.dispatch({
          type:       VideoActionTypes.BULK_RESTORE_TRASH_VIDEOS_FAIL,
          tableScope: table.scope,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to restore videos', 'error');
      } else {
        AppDispatcher.dispatch({
          type:       VideoActionTypes.BULK_RESTORE_TRASH_VIDEOS_DONE,
          tableScope: table.scope,
        });

        GlobalContainer.notify(
          'Bulk restore in progress, this may take some time.',
          'success',
        );
      }
    });
  },

  bulkDeleteTrashVideos(table, trashVideos, bulkDeleteData) {
    const staffRestriction = getRestrictionScope();
    const scope = localStorage.ownerScope || staffRestriction || 'all';
    const isAllVideos = typeof trashVideos === 'string';

    AppDispatcher.dispatch({
      type:       VideoActionTypes.BULK_DELETE_TRASH_VIDEOS,
      tableScope: table.scope,
      trashVideos,
      bulkDeleteData,
    });

    const deleteData = {
      video_scope: table.scope,
      scope:       scope,
      ids:         isAllVideos ? 'all' : _lodash.map(trashVideos, 'id'),
    };

    const data = { ...deleteData, ...bulkDeleteData };

    bulkDeleteVideosRequest = APIRequest.delete({
      resource: '/v1/video_bulk_actions/permanently_delete_videos',
      data,
    });

    bulkDeleteVideosRequest.end((error) => {
      if (error) {
        AppDispatcher.dispatch({
          type:       VideoActionTypes.BULK_DELETE_TRASH_VIDEOS_FAIL,
          tableScope: table.scope,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to delete videos', 'error');
      } else {
        AppDispatcher.dispatch({
          type:       VideoActionTypes.BULK_DELETE_TRASH_VIDEOS_DONE,
          tableScope: table.scope,
        });

        GlobalContainer.notify(
          'Bulk delete in progress, this may take some time.',
          'success',
        );
      }
    });
  }
};

export default VideoActions;
